import { useEffect, useState } from "react";
import { getAuth, applyActionCode, verifyPasswordResetCode, confirmPasswordReset } from "firebase/auth";
import styles from "./AuthActionPage.module.css";
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { IconButton, InputAdornment, Typography, TextField, Button } from '@mui/material';

export default function AuthActionPage() {
  const [status, setStatus] = useState("processing");
  const [mode, setMode] = useState(null);
  const [oobCode, setOobCode] = useState(null);
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const modeParam = params.get("mode");
    const code = params.get("oobCode");
    setMode(modeParam);
    setOobCode(code);

    const auth = getAuth();

    if (!modeParam || !code) {
      setStatus("error");
      return;
    }

    if (modeParam === "verifyEmail") {
      applyActionCode(auth, code)
        .then(() => setStatus("verify-success"))
        .catch(() => setStatus("verify-error"));
    } else if (modeParam === "resetPassword") {
      verifyPasswordResetCode(auth, code)
        .then(email => {
          setEmail(email);
          setStatus("reset-form");
        })
        .catch(() => setStatus("reset-error"));
    }
    // Add more modes as needed
  }, []);

  const handleReset = async (e) => {
    e.preventDefault();
    setFormError("");
    if (newPassword !== confirmPassword) {
      setFormError("Passwords do not match. Please try again.");
      return;
    }
    if (!newPassword) {
      setFormError("Please enter a new password.");
      return;
    }
    const auth = getAuth();
    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      setStatus("reset-success");
    } catch {
      setStatus("reset-error");
    }
  };

  // Main Navbar (copied from LoginPage)
  const Navbar = () => (
    <header className={styles.navbar}>
      <div className={styles.navbarContent}>
        <img
          className={styles.navLogo}
          alt="GigemAI Logo"
          src="gigemai_logo_v2.png"
        />
      </div>
    </header>
  );

  return (
    <main className={styles.authActionPage}>
      <Navbar />
      <section className={styles.authContent}>
        {status === "processing" && (
          <Typography className={styles.authMessage}>Processing...</Typography>
        )}
        {status === "verify-success" && (
          <Typography className={styles.authMessage + " " + styles.authSuccess}>
            Email verified! You can now log in.
          </Typography>
        )}
        {status === "verify-error" && (
          <Typography className={styles.authMessage + " " + styles.authError}>
            Verification failed or link expired.
          </Typography>
        )}
        {status === "reset-form" && (
          <form className={styles.authFormContainer} onSubmit={handleReset}>
            <Typography className={styles.authMessage}>Reset your password for {email}</Typography>
            <div className={styles.authFormFields}>
              <div className={styles.authFormGroup}>
                <Typography
                  sx={{
                    color: '#1E1E1E',
                    fontSize: '16px',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 400,
                    lineHeight: '22.4px',
                    marginBottom: '8px',
                  }}
                >
                  New Password
                </Typography>
                <TextField
                  placeholder="Enter new password"
                  variant="outlined"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={e => { setNewPassword(e.target.value); setFormError(""); }}
                  fullWidth
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      '& fieldset': { borderColor: '#D9D9D9' },
                      '&:hover fieldset': { borderColor: '#D9D9D9' },
                      '&.Mui-focused fieldset': { borderColor: '#D9D9D9' },
                    },
                    '& .MuiOutlinedInput-input': {
                      fontSize: '16px',
                      fontFamily: 'Inter, sans-serif',
                      color: '#000000',
                    },
                    '& .MuiOutlinedInput-input::placeholder': {
                      color: '#B3B3B3',
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                          {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </div>
              <div className={styles.authFormGroup}>
                <Typography
                  sx={{
                    color: '#1E1E1E',
                    fontSize: '16px',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 400,
                    lineHeight: '22.4px',
                    marginBottom: '8px',
                  }}
                >
                  Confirm New Password
                </Typography>
                <TextField
                  placeholder="Re-enter new password"
                  variant="outlined"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={e => { setConfirmPassword(e.target.value); setFormError(""); }}
                  fullWidth
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      '& fieldset': { borderColor: '#D9D9D9' },
                      '&:hover fieldset': { borderColor: '#D9D9D9' },
                      '&.Mui-focused fieldset': { borderColor: '#D9D9D9' },
                    },
                    '& .MuiOutlinedInput-input': {
                      fontSize: '16px',
                      fontFamily: 'Inter, sans-serif',
                      color: '#000000',
                    },
                    '& .MuiOutlinedInput-input::placeholder': {
                      color: '#B3B3B3',
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowConfirmPassword((prev) => !prev)} edge="end">
                          {showConfirmPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </div>
            </div>
            <Button
              variant="contained"
              type="submit"
              className={styles.authButton}
              fullWidth
            >
              Reset Password
            </Button>
            {formError && (
              <Typography className={styles.authError}>{formError}</Typography>
            )}
          </form>
        )}
        {status === "reset-success" && (
          <Typography className={styles.authMessage + " " + styles.authSuccess}>
            Password reset! You can now log in.
          </Typography>
        )}
        {status === "reset-error" && (
          <Typography className={styles.authMessage + " " + styles.authError}>
            Password reset failed or link expired.
          </Typography>
        )}
        {status === "error" && (
          <Typography className={styles.authMessage + " " + styles.authError}>
            Unknown action or invalid link.
          </Typography>
        )}
      </section>
    </main>
  );
} 